import { useState, useEffect, useRef, useMemo, useId } from "react";
// Doit précéder les grammaires : c'est ce module qui publie `Prism` en global.
import Prism from "../lib/prism";
// Charge les langages Prism les plus courants
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-jsx";
import "prismjs/components/prism-tsx";
import "prismjs/components/prism-json";
import "prismjs/components/prism-css";
import "prismjs/components/prism-python";
import "prismjs/components/prism-go";
import "prismjs/components/prism-rust";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-yaml";

import Editor, { useMonaco } from "@monaco-editor/react";
import { AnimatedCopyIcon } from "@one-nexty/icons";
import { cn } from "../lib/cn";
import { Badge } from "./badge";
import { Button } from "./button";

export type CodeEditorFrame = "macos" | "sobre" | "glass";
export type CodeEditorMode = "monaco" | "interactive" | "viewer" | "diff";

export interface ExtraLib {
  content: string;
  filePath?: string;
}

export interface CodeEditorProps {
  /** Code source à afficher ou éditer */
  value?: string;
  /** Code source alternatif / version originale (mode diff) */
  originalValue?: string;
  /** Callback déclenché à la modification de code (modes monaco et interactive) */
  onChange?: (val: string) => void;
  /** Langage (typescript, javascript, tsx, html, css, json, python, go, rust, bash, sql, etc.) */
  language?: string;
  /** Titre ou nom de fichier affiché dans la barre (ex: "app.tsx", "server.go") */
  title?: string;
  /** Style de la fenêtre : "macos" (traffic lights), "sobre" (minimaliste), "glass" (translucide) */
  frame?: CodeEditorFrame;
  /** Mode d'affichage/édition : "monaco" (monaco interactif), "interactive" (editeur rapide), "viewer" (lecture seule), "diff" (comparateur) */
  mode?: CodeEditorMode;
  /** Afficher les numéros de ligne (défaut: true) */
  showLineNumbers?: boolean;
  /** Afficher la mini-carte de code / minimap (défaut: false) */
  showMinimap?: boolean;
  /** Afficher la barre de statut en bas (défaut: true) */
  showStatusBar?: boolean;
  /** Hauteur personnalisée (ex: "400px", "100%", "auto") */
  height?: string | number;
  /** Lignes en surbrillation (ex: [3, 4, 8]) */
  highlightLines?: number[];
  /** Thème forcé ("dark" | "light" | "auto") */
  theme?: "dark" | "light" | "auto";
  /** Afficher le bouton de copie */
  showCopyButton?: boolean;
  /** Classes CSS supplémentaires pour le conteneur principal */
  className?: string;
  /** Descriptif ou sous-titre de l'éditeur */
  description?: string;
  /** Déclarations de types supplémentaires (.d.ts) injectées dans Monaco TypeScript Language Service */
  extraLibs?: ExtraLib[] | string;
  /** Ignorer les erreurs d'imports non résolus et variables globales d'extraits (défaut: true) */
  ignoreMissingImports?: boolean;
  /** Liste spécifique des codes d'erreur TypeScript à ignorer dans Monaco */
  diagnosticCodesToIgnore?: number[];
  /** Options de compilation TypeScript personnalisées transmises à Monaco */
  compilerOptions?: Record<string, any>;
}

/**
 * Composant d'icône officielle pour les langages de programmation.
 */
export function LanguageIcon({
  language,
  size = 15,
  className,
}: {
  language: string;
  size?: number;
  className?: string;
}) {
  const lang = language.toLowerCase();

  if (lang === "typescript" || lang === "ts") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <rect width="24" height="24" rx="4" fill="#3178C6" />
        <path
          d="M11.5 16.5v-7h-2.5v-1.5h6.5v1.5h-2.5v7h-1.5zm6-1.5c.5 0 1-.2 1.3-.5l.9.9c-.6.6-1.4.9-2.3.9-1.8 0-3-1.1-3-2.8 0-1.7 1.2-2.8 2.9-2.8.9 0 1.6.3 2.1.8l-.8 1c-.4-.4-.9-.6-1.4-.6-.9 0-1.4.6-1.4 1.5 0 1 .6 1.6 1.7 1.6z"
          fill="#FFF"
        />
      </svg>
    );
  }

  if (lang === "tsx" || lang === "jsx" || lang === "react") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <ellipse cx="12" cy="12" rx="10" ry="4.2" stroke="#61DAFB" strokeWidth="1.5" transform="rotate(0 12 12)" />
        <ellipse cx="12" cy="12" rx="10" ry="4.2" stroke="#61DAFB" strokeWidth="1.5" transform="rotate(60 12 12)" />
        <ellipse cx="12" cy="12" rx="10" ry="4.2" stroke="#61DAFB" strokeWidth="1.5" transform="rotate(120 12 12)" />
        <circle cx="12" cy="12" r="2" fill="#61DAFB" />
      </svg>
    );
  }

  if (lang === "javascript" || lang === "js") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <rect width="24" height="24" rx="4" fill="#F7DF1E" />
        <path
          d="M13.5 17c.5.3 1.1.5 1.8.5 1 0 1.5-.4 1.5-1.1 0-.7-.5-1-1.6-1.4l-.5-.2c-1.4-.5-2.2-1.3-2.2-2.7 0-1.7 1.4-2.8 3.5-2.8 1.2 0 2.2.3 2.8.7l-.7 1.4c-.5-.3-1.3-.6-2.1-.6-.9 0-1.4.4-1.4 1 0 .6.5.9 1.5 1.3l.5.2c1.6.6 2.3 1.4 2.3 2.8 0 1.8-1.4 2.9-3.7 2.9-1.4 0-2.5-.4-3.1-.9l.7-1.3zM7.5 17.2c.6.4 1.4.7 2.3.7 1.3 0 2.1-.6 2.1-2.1v-6.3h-1.8v6.2c0 .6-.3.9-.9.9-.5 0-1-.2-1.3-.4l-.4 1.5z"
          fill="#000"
        />
      </svg>
    );
  }

  if (lang === "python" || lang === "py") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path
          d="M11.9 2c-5.2 0-4.9 2.3-4.9 2.3v2.4h5v.7H5.2S2 7 2 12.3c0 5.2 2.8 5 2.8 5h1.7v-2.4c0-2.8 2.4-2.8 2.4-2.8h4.9s2.4.1 2.4-2.3V4.3S17.1 2 11.9 2zm-2.7 1.7a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
          fill="#3776AB"
        />
        <path
          d="M12.1 22c5.2 0 4.9-2.3 4.9-2.3v-2.4h-5v-.7h6.8s3.2.4 3.2-4.9c0-5.2-2.8-5-2.8-5h-1.7v2.4c0 2.8-2.4 2.8-2.4 2.8h-4.9s-2.4-.1-2.4 2.3v5.6s-.9 2.3 4.3 2.3zm2.7-1.7a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
          fill="#FFD43B"
        />
      </svg>
    );
  }

  if (lang === "go" || lang === "golang") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path
          d="M1.8 10.5c.3 0 .6-.2.7-.5.1-.4 0-.8-.3-1.1-.3-.2-.7-.2-1 0-.3.3-.4.7-.2 1 .2.4.5.6.8.6zm21.3 2.6c-.2-.1-.5-.1-.7 0-.3.2-.4.5-.3.8.1.3.4.5.7.5.3 0 .6-.2.7-.5.1-.3 0-.6-.4-.8zm-4.6-5.8c-.3-.2-.6-.1-.8.1-.2.3-.1.7.2.8.3.2.6.1.8-.2.2-.2.1-.6-.2-.7zm-14 9c-.2-.2-.6-.2-.8 0-.2.2-.2.6 0 .8.2.2.6.2.8 0 .2-.2.2-.6 0-.8zm15.4-4.8c-.8-.4-1.7-.5-2.6-.4-.4-1.7-1.5-3.1-3.1-3.8-2.4-1-5.1-.2-6.5 1.8-.7 1-1 2.2-.9 3.5-1.2.4-2.2 1.4-2.5 2.7-.4 1.7.4 3.4 2 4.1 1.7.7 3.6.1 4.5-1.3.5.2 1 .3 1.6.3 2.6 0 4.8-1.8 5.3-4.3.7 0 1.4-.2 2.1-.6.5-.4.8-1 1-1.5-.3-.2-.6-.4-.9-.5z"
          fill="#00ADD8"
        />
      </svg>
    );
  }

  if (lang === "rust" || lang === "rs") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
        <circle cx="12" cy="12" r="9" strokeDasharray="3 2" />
        <path d="M9 9h3.5a2 2 0 0 1 0 4H9v2m3-2l2.5 4M9 9v6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (lang === "html") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path
          d="M3 2l1.6 18L12 22l7.4-2L21 2H3zm14.7 5.5h-8.8l.2 2.3h8.4l-.6 6.7-4.9 1.4-4.9-1.4-.3-3.7h2.3l.1 1.9 2.8.8 2.8-.8.3-3.2H6.4l-.7-7.3h12.2l-.2 2.3z"
          fill="#E34F26"
        />
      </svg>
    );
  }

  if (lang === "css") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path
          d="M3 2l1.6 18L12 22l7.4-2L21 2H3zm14.7 5.5H8.6l.2 2.3h8.3l-.6 6.7-4.5 1.4-4.5-1.4-.3-3.7h2.2l.1 1.9 2.5.7 2.5-.7.3-3.2H6.4l-.7-7.3h12l-.3 2.3z"
          fill="#1572B6"
        />
      </svg>
    );
  }

  if (lang === "sql") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#336791" strokeWidth="1.75" className={className}>
        <ellipse cx="12" cy="5" rx="8" ry="3" />
        <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
      </svg>
    );
  }

  if (lang === "json") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <path d="M8 3H6a2 2 0 0 0-2 2v3a2 2 0 0 1-2 2 2 2 0 0 1 2 2v3a2 2 0 0 0 2 2h2m8-18h2a2 2 0 0 1 2 2v3a2 2 0 0 0 2 2 2 2 0 0 0-2 2v3a2 2 0 0 1-2 2h-2" />
      </svg>
    );
  }

  if (lang === "bash" || lang === "shell" || lang === "sh") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <polyline points="4 17 10 11 4 5" />
        <line x1="12" y1="19" x2="20" y2="19" />
      </svg>
    );
  }

  if (lang === "markdown" || lang === "md") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M7 15V9l3 3 3-3v6m3 0v-6l2 2m0 0l2-2" />
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="m10 13-2 2 2 2m4-4 2 2-2 2" />
    </svg>
  );
}

/**
 * Configure les thèmes Monaco personnalisés intégrés au design system Nexty
 */
const setupMonacoTheme = (monaco: any) => {
  monaco.editor.defineTheme("nexty-dark", {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "", foreground: "E2E8F0", background: "0B0F19" },
      { token: "keyword", foreground: "A855F7", fontStyle: "bold" },
      { token: "custom-info", foreground: "3B82F6" },
      { token: "string", foreground: "10B981" },
      { token: "number", foreground: "F59E0B" },
      { token: "regexp", foreground: "EC4899" },
      { token: "type", foreground: "06B6D4" },
      { token: "comment", foreground: "64748B", fontStyle: "italic" },
      { token: "delimiter", foreground: "94A3B8" },
      { token: "tag", foreground: "F43F5E" },
      { token: "attribute.name", foreground: "38BDF8" },
      { token: "function", foreground: "60A5FA" },
      { token: "variable", foreground: "F1F5F9" },
    ],
    colors: {
      "editor.background": "#0F172A",
      "editor.foreground": "#E2E8F0",
      "editor.lineHighlightBackground": "#1E293B60",
      "editorCursor.foreground": "#3B82F6",
      "editorWhitespace.foreground": "#334155",
      "editorIndentGuide.background": "#1E293B",
      "editorIndentGuide.activeBackground": "#3B82F680",
      "editorLineNumber.foreground": "#475569",
      "editorLineNumber.activeForeground": "#94A3B8",
      "editor.selectionBackground": "#3B82F640",
      "editor.inactiveSelectionBackground": "#3B82F620",
    },
  });

  monaco.editor.defineTheme("nexty-light", {
    base: "vs",
    inherit: true,
    rules: [
      { token: "", foreground: "1E293B", background: "F8FAFC" },
      { token: "keyword", foreground: "7C3AED", fontStyle: "bold" },
      { token: "string", foreground: "059669" },
      { token: "number", foreground: "D97706" },
      { token: "comment", foreground: "94A3B8", fontStyle: "italic" },
      { token: "type", foreground: "0891B2" },
      { token: "tag", foreground: "E11D48" },
      { token: "attribute.name", foreground: "0284C7" },
      { token: "function", foreground: "2563EB" },
    ],
    colors: {
      "editor.background": "#F8FAFC",
      "editor.foreground": "#1E293B",
      "editor.lineHighlightBackground": "#F1F5F9",
      "editorCursor.foreground": "#2563EB",
      "editorWhitespace.foreground": "#E2E8F0",
      "editorLineNumber.foreground": "#94A3B8",
      "editorLineNumber.activeForeground": "#475569",
      "editor.selectionBackground": "#2563EB30",
    },
  });
};

const PRISM_UNIFIED_THEME_CSS = `
  .nx-code-theme .token.keyword,
  .nx-code-theme .token.rule { color: #a855f7; font-weight: 600; }
  .dark .nx-code-theme .token.keyword,
  .dark .nx-code-theme .token.rule { color: #a855f7; }
  :root:not(.dark) .nx-code-theme .token.keyword,
  :root:not(.dark) .nx-code-theme .token.rule { color: #7c3aed; }

  .nx-code-theme .token.string,
  .nx-code-theme .token.char,
  .nx-code-theme .token.builtin { color: #10b981; }
  .dark .nx-code-theme .token.string { color: #10b981; }
  :root:not(.dark) .nx-code-theme .token.string { color: #059669; }

  .nx-code-theme .token.function,
  .nx-code-theme .token.method { color: #60a5fa; }
  .dark .nx-code-theme .token.function { color: #60a5fa; }
  :root:not(.dark) .nx-code-theme .token.function { color: #2563eb; }

  .nx-code-theme .token.class-name,
  .nx-code-theme .token.type-annotation,
  .nx-code-theme .token.symbol { color: #06b6d4; font-weight: 500; }
  .dark .nx-code-theme .token.class-name { color: #06b6d4; }
  :root:not(.dark) .nx-code-theme .token.class-name { color: #0891b2; }

  .nx-code-theme .token.number,
  .nx-code-theme .token.boolean,
  .nx-code-theme .token.constant { color: #f59e0b; }
  .dark .nx-code-theme .token.number { color: #f59e0b; }
  :root:not(.dark) .nx-code-theme .token.number { color: #d97706; }

  .nx-code-theme .token.tag { color: #f43f5e; font-weight: 500; }
  .dark .nx-code-theme .token.tag { color: #f43f5e; }
  :root:not(.dark) .nx-code-theme .token.tag { color: #e11d48; }

  .nx-code-theme .token.attr-name,
  .nx-code-theme .token.property { color: #38bdf8; }
  .dark .nx-code-theme .token.attr-name { color: #38bdf8; }
  :root:not(.dark) .nx-code-theme .token.attr-name { color: #0284c7; }

  .nx-code-theme .token.comment,
  .nx-code-theme .token.prolog,
  .nx-code-theme .token.doctype { color: #64748b; font-style: italic; }
  .dark .nx-code-theme .token.comment { color: #64748b; }
  :root:not(.dark) .nx-code-theme .token.comment { color: #94a3b8; }

  .nx-code-theme .token.operator,
  .nx-code-theme .token.punctuation,
  .nx-code-theme .token.delimiter { color: #94a3b8; }
  .dark .nx-code-theme .token.operator { color: #94a3b8; }
  :root:not(.dark) .nx-code-theme .token.operator { color: #64748b; }
`;

function splitHtmlIntoLines(html: string): string[] {
  const lines: string[] = [];
  let currentLine = "";
  const openTags: string[] = [];

  const regex = /(<[^>]+>)|([^<]+)/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(html)) !== null) {
    if (match[1]) {
      const tag = match[1];
      if (tag.startsWith("</")) {
        openTags.pop();
        currentLine += tag;
      } else if (!tag.endsWith("/>") && !tag.startsWith("<img") && !tag.startsWith("<br") && !tag.startsWith("<hr")) {
        openTags.push(tag);
        currentLine += tag;
      } else {
        currentLine += tag;
      }
    } else if (match[2]) {
      const text = match[2];
      const parts = text.split("\n");
      for (let i = 0; i < parts.length; i++) {
        currentLine += parts[i];
        if (i < parts.length - 1) {
          for (let j = openTags.length - 1; j >= 0; j--) {
            const openTag = openTags[j];
            if (openTag) {
              const tagMatch = openTag.match(/<([a-z0-9-]+)/i);
              if (tagMatch && tagMatch[1]) {
                currentLine += `</${tagMatch[1]}>`;
              }
            }
          }
          lines.push(currentLine);
          currentLine = openTags.join("");
        }
      }
    }
  }
  lines.push(currentLine);
  return lines;
}

/**
 * Composant d'Éditeur & Colorateur de Code SOTA pour le Design System Nexty.
 */
export function CodeEditor({
  value = "",
  originalValue = "",
  onChange,
  language = "typescript",
  title,
  frame = "macos",
  mode = "monaco",
  showLineNumbers = true,
  showMinimap = false,
  showStatusBar = true,
  height = 360,
  highlightLines = [],
  theme: explicitTheme = "auto",
  showCopyButton = true,
  className,
  description,
  extraLibs,
  ignoreMissingImports = true,
  diagnosticCodesToIgnore,
  compilerOptions,
}: CodeEditorProps) {
  const [code, setCode] = useState(value);
  const [origCode, setOrigCode] = useState(originalValue);
  const [copied, setCopied] = useState(false);
  const [minimapVisible, setMinimapVisible] = useState(showMinimap);
  const [isDark, setIsDark] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const monaco = useMonaco();
  const instanceId = useId();

  useEffect(() => {
    setCode(value);
  }, [value]);

  useEffect(() => {
    setOrigCode(originalValue);
  }, [originalValue]);

  useEffect(() => {
    if (explicitTheme !== "auto") {
      setIsDark(explicitTheme === "dark");
      return;
    }

    const checkTheme = () => {
      const isDocDark = document.documentElement.classList.contains("dark");
      setIsDark(isDocDark);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [explicitTheme]);

  // Nom de langage officiel reconnu par le tokenizer Monarch de Monaco ("typescript" ou "javascript")
  const monacoLanguage = useMemo(() => {
    const l = language.toLowerCase();
    if (l === "tsx" || l === "typescript" || l === "ts") return "typescript";
    if (l === "jsx" || l === "javascript" || l === "js") return "javascript";
    return l;
  }, [language]);

  // Nom de fichier affiché
  const displayTitle = useMemo(() => {
    if (title) return title;
    const extMap: Record<string, string> = {
      typescript: "App.tsx",
      javascript: "index.js",
      tsx: "Component.tsx",
      jsx: "View.jsx",
      html: "index.html",
      css: "styles.css",
      json: "config.json",
      python: "script.py",
      go: "main.go",
      rust: "main.rs",
      sql: "query.sql",
      bash: "deploy.sh",
      shell: "run.sh",
      markdown: "README.md",
    };
    return extMap[language.toLowerCase()] || `file.${language}`;
  }, [title, language]);

  // URI de modèle virtuelle unique par instance pour éviter tout conflit entre éditeurs sur la même page
  const uniqueModelPath = useMemo(() => {
    const safeId = instanceId.replace(/[^a-zA-Z0-9_]/g, "_");
    const l = language.toLowerCase();
    const ext = (l === "tsx" || l === "typescript" || l === "ts" || l === "jsx" || l === "javascript" || l === "js") ? "tsx" : l;
    return `inmemory://model_${safeId}.${ext}`;
  }, [instanceId, language]);

  // Handler de configuration de Monaco appelé AVANT la création du modèle
  const handleBeforeMount = (monacoInstance: any) => {
    setupMonacoTheme(monacoInstance);

    const tsLang = monacoInstance.languages?.typescript;
    if (tsLang) {
      // Configuration native VS Code : mode React Classic (React.createElement)
      // Évite la dépendance au module 'react/jsx-runtime' tout en autorisant 100% la syntaxe JSX/TSX
      const config = {
        jsx: tsLang.JsxEmit?.React ?? 2,
        target: tsLang.ScriptTarget?.Latest ?? 99,
        allowNonTsExtensions: true,
        moduleResolution: tsLang.ModuleResolutionKind?.NodeJs ?? 2,
        allowSyntheticDefaultImports: true,
        esModuleInterop: true,
        allowJs: true,
        noEmit: true,
        ...compilerOptions,
      };

      // Mode snippet VS Code : validation de syntaxe stricte active, validation sémantique (node_modules absents) débrayée si ignoreMissingImports est vrai.
      const diagOptions = {
        noSyntaxValidation: false,
        noSemanticValidation: ignoreMissingImports,
        ...(diagnosticCodesToIgnore ? { diagnosticCodesToIgnore } : {}),
      };

      if (tsLang.typescriptDefaults) {
        tsLang.typescriptDefaults.setCompilerOptions(config);
        tsLang.typescriptDefaults.setDiagnosticsOptions(diagOptions);

        if (extraLibs) {
          if (typeof extraLibs === "string") {
            tsLang.typescriptDefaults.addExtraLib(extraLibs, `file:///node_modules/@types/custom_${instanceId}/index.d.ts`);
          } else if (Array.isArray(extraLibs)) {
            extraLibs.forEach((lib: ExtraLib, idx: number) => {
              const path = lib.filePath || `file:///node_modules/@types/custom_lib_${instanceId}_${idx}/index.d.ts`;
              tsLang.typescriptDefaults.addExtraLib(lib.content, path);
            });
          }
        }
      }

      if (tsLang.javascriptDefaults) {
        tsLang.javascriptDefaults.setCompilerOptions(config);
        tsLang.javascriptDefaults.setDiagnosticsOptions(diagOptions);
      }
    }
  };

  // Effet de mise à jour pour Monaco lorsque monaco hook est disponible
  useEffect(() => {
    if (!monaco) return;
    handleBeforeMount(monaco);
  }, [monaco, extraLibs, ignoreMissingImports, diagnosticCodesToIgnore, compilerOptions]);

  const handleCodeChange = (newVal: string) => {
    setCode(newVal);
    onChange?.(newVal);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      console.error("Échec de la copie :", err);
    }
  };

  const linesCount = useMemo(() => {
    return code ? code.split("\n").length : 1;
  }, [code]);

  const stats = useMemo(() => {
    const charCount = code.length;
    const sizeKb = (new Blob([code]).size / 1024).toFixed(1);
    return { charCount, sizeKb };
  }, [code]);

  const highlightedHtml = useMemo(() => {
    const lang = language.toLowerCase();
    const prismGrammar =
      Prism.languages[lang] ||
      Prism.languages.tsx ||
      Prism.languages.typescript ||
      Prism.languages.javascript;
    if (!prismGrammar) return code;
    try {
      return Prism.highlight(code || "", prismGrammar, lang);
    } catch {
      return code;
    }
  }, [code, language]);

  const highlightedLines = useMemo(() => {
    return splitHtmlIntoLines(highlightedHtml);
  }, [highlightedHtml]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "group relative flex w-full flex-col overflow-hidden isolate transition-all duration-300",
        frame === "macos" && "rounded-2xl border border-border/80 shadow-2xl bg-surface-sunken",
        frame === "sobre" && "rounded-xl border border-border/70 bg-surface-sunken shadow-md",
        frame === "glass" && "rounded-2xl border border-border/60 bg-surface-raised/40 backdrop-blur-xl shadow-xl",
        className
      )}
      style={{
        height: height,
      }}
    >
      {/* ------------------------------------------------------------------ En-tête de la fenêtre */}
      <div className="flex h-11 shrink-0 items-center justify-between border-b border-border/70 bg-surface-raised/60 px-3.5 backdrop-blur-md select-none rounded-t-[inherit]">
        <div className="flex items-center gap-3">
          {/* macOS Traffic lights buttons */}
          {frame === "macos" && (
            <div className="flex items-center gap-1.5 pr-1">
              <span className="size-3 rounded-full bg-red-500/90 shadow-sm transition-transform hover:scale-110" />
              <span className="size-3 rounded-full bg-amber-500/90 shadow-sm transition-transform hover:scale-110" />
              <span className="size-3 rounded-full bg-emerald-500/90 shadow-sm transition-transform hover:scale-110" />
            </div>
          )}

          {/* Logo du langage et nom du fichier */}
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-medium text-fg flex items-center gap-2">
              <LanguageIcon language={language} size={15} />
              {displayTitle}
            </span>
            {description && (
              <span className="hidden sm:inline-block text-[11px] text-fg-muted/80 truncate max-w-[200px]">
                — {description}
              </span>
            )}
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-1.5">
          {/* Toggle Minimap */}
          {mode === "monaco" && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setMinimapVisible((v) => !v)}
              title={minimapVisible ? "Masquer la minimap" : "Afficher la minimap"}
              className="text-fg-muted hover:text-fg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M15 3v18"/>
              </svg>
            </Button>
          )}

          {/* Bouton Copier Icône Seule avec AnimatedCopyIcon */}
          {showCopyButton && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleCopy}
              className={cn(
                "transition-all duration-300",
                copied
                  ? "text-emerald-500 dark:text-emerald-400 bg-emerald-500/10"
                  : "text-fg-muted hover:text-fg"
              )}
              title={copied ? "Copié !" : "Copier le code"}
              aria-label="Copier le code"
            >
              <AnimatedCopyIcon copied={copied} size={15} />
            </Button>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ Zone de Code principale */}
      <div className="relative min-h-0 flex-1 overflow-hidden bg-surface-sunken nx-code-theme">
        <style>{PRISM_UNIFIED_THEME_CSS}</style>

        {/* MODE 1 : MONACO EDITOR */}
        {mode === "monaco" && (
          <Editor
            height="100%"
            path={uniqueModelPath}
            language={monacoLanguage}
            theme={isDark ? "nexty-dark" : "nexty-light"}
            value={code}
            beforeMount={handleBeforeMount}
            onChange={(val) => handleCodeChange(val || "")}
            options={{
              fontSize: 13,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              minimap: { enabled: minimapVisible },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              smoothScrolling: true,
              cursorBlinking: "smooth",
              cursorSmoothCaretAnimation: "on",
              lineNumbers: showLineNumbers ? "on" : "off",
              lineNumbersMinChars: 3,
              renderLineHighlight: "all",
              roundedSelection: true,
              padding: { top: 12, bottom: 12 },
              tabSize: 2,
            }}
          />
        )}

        {/* MODE 2 : INTERACTIVE (Monaco interactif avec édition directe) */}
        {mode === "interactive" && (
          <Editor
            height="100%"
            path={uniqueModelPath}
            language={monacoLanguage}
            theme={isDark ? "nexty-dark" : "nexty-light"}
            value={code}
            beforeMount={handleBeforeMount}
            onChange={(val) => handleCodeChange(val || "")}
            options={{
              fontSize: 13,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              smoothScrolling: true,
              lineNumbers: showLineNumbers ? "on" : "off",
              lineNumbersMinChars: 3,
              renderLineHighlight: "all",
              roundedSelection: true,
              padding: { top: 12, bottom: 12 },
              tabSize: 2,
            }}
          />
        )}

        {/* MODE 3 : VIEWER (Tableau Ligne par Ligne 100% aligné & 1 seul ascenseur) */}
        {mode === "viewer" && (
          <div className="h-full w-full overflow-auto bg-surface-sunken">
            <table className="w-full border-collapse font-mono text-xs leading-6">
              <tbody>
                {highlightedLines.map((lineHtml, idx) => {
                  const lineNum = idx + 1;
                  const isHighlighted = highlightLines.includes(lineNum);
                  return (
                    <tr
                      key={idx}
                      className={cn(
                        "transition-colors hover:bg-surface-raised/30",
                        isHighlighted && "bg-accent/10"
                      )}
                    >
                      {showLineNumbers && (
                        <td className="sticky left-0 z-10 w-12 shrink-0 select-none border-r border-border/40 bg-surface-sunken px-3 py-0.5 text-right font-mono text-[11px] text-fg-muted/50 align-top">
                          <span className={cn(isHighlighted && "font-bold text-accent")}>
                            {lineNum}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-0.5 align-top whitespace-pre font-mono text-fg">
                        <span dangerouslySetInnerHTML={{ __html: lineHtml || " " }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* MODE 4 : DIFF (Comparateur de Code côte à côte - 1 seul ascenseur) */}
        {mode === "diff" && (
          <div className="grid h-full grid-cols-2 divide-x divide-border/60 overflow-auto font-mono text-xs leading-6">
            <div className="flex flex-col bg-red-950/10 p-4">
              <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-rose-500">
                Original
              </div>
              <pre className="m-0 font-mono text-xs leading-6 text-fg-muted whitespace-pre">
                <code>{origCode}</code>
              </pre>
            </div>
            <div className="flex flex-col bg-emerald-950/10 p-4">
              <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-emerald-500">
                Modifié
              </div>
              <pre className="m-0 text-fg whitespace-pre font-mono text-xs leading-6">
                <code>{code}</code>
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ Barre de Statut */}
      {showStatusBar && (
        <div className="flex h-7 shrink-0 items-center justify-between border-t border-border/60 bg-surface-raised/70 px-3.5 text-[11px] font-mono text-fg-muted select-none rounded-b-[inherit]">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-fg font-medium">
              <LanguageIcon language={language} size={13} />
              <span className="uppercase">{language}</span>
            </span>
            <span>•</span>
            <span>
              {linesCount} {linesCount > 1 ? "lignes" : "ligne"}
            </span>
            <span className="hidden sm:inline-block">•</span>
            <span className="hidden sm:inline-block">{stats.sizeKb} KB</span>
          </div>

          <div className="flex items-center gap-3">
            <Badge tone="neutral" className="px-1.5 py-0 text-[10px] rounded">
              UTF-8
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
}
